(() => {
  if (window.ieGeekListingInitialized) return;
  window.ieGeekListingInitialized = true;
  const mobile = window.matchMedia('(max-width: 767px)');
  const money = (amount, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: currency || 'EUR' }).format(amount / 100);

  let cleanupSections;
  let cleanupComparison;
  let activeComparison;
  let comparisonSource;

  function init() {
    const product = document.querySelector('[data-listing-product]');
    if (!product) return;
    initSections();
    if (product.dataset.listingInitialized) return;
    product.dataset.listingInitialized = 'true';
    document.documentElement.classList.add('listing-page');
    const cleanupGallery = initGallery(product);
    const purchase = product.querySelector('[data-listing-purchase]');
    const sticky = product.querySelector('[data-listing-sticky]');
    const add = purchase?.querySelector('button[name="add"]');
    const buy = purchase?.querySelector('.buy-now-button');
    const picker = product.querySelector('variant-selects');
    // Variant refreshes can remove the native class before an add request finishes.
    const isLoading = button => !!button && (button.classList.contains('loading') || (button === add && button.getAttribute('aria-disabled') === 'true'));
    const syncSticky = () => {
      const available = product.dataset.listingAvailability === 'available';
      if (!available && add && !add.disabled) add.disabled = true;
      // Buy Now has its own request lifecycle; stock and variant refreshes must also gate it.
      const buyDisabled = !available || !add || add.disabled || isLoading(add) || isLoading(buy);
      if (buy && buy.disabled !== buyDisabled) buy.disabled = buyDisabled;
      [add, buy].forEach(button => {
        if (!button) return;
        const loading = isLoading(button);
        if (button.classList.contains('loading') !== loading) button.classList.toggle('loading', loading);
        button.setAttribute('aria-busy', String(loading));
      });
      sticky?.querySelectorAll('[data-listing-action]').forEach(button => {
        const target = button.dataset.listingAction === 'add' ? add : buy;
        const loading = isLoading(target);
        if (button.dataset.listingAction === 'add') {
          const text = add?.querySelector('.single-add-to-cart-button--text')?.textContent.trim();
          const label = button.querySelector('span:not(.loading-overlay)');
          if (label && text && label.textContent !== text) label.textContent = text;
        }
        button.classList.toggle('loading', loading);
        button.setAttribute('aria-busy', String(loading));
        button.disabled = !target || target.disabled || loading || target.getAttribute('aria-disabled') === 'true';
      });
    };
    syncSticky();
    const buttonObserver = new MutationObserver(syncSticky);
    if (add) buttonObserver.observe(add, { attributes: true, attributeFilter: ['disabled', 'class', 'aria-disabled'], childList: true, characterData: true, subtree: true });
    if (buy) buttonObserver.observe(buy, { attributes: true, attributeFilter: ['disabled', 'class'] });
    const preventRepeatedAdd = event => {
      if (product.dataset.listingAvailability === 'available' && add && !add.disabled && !isLoading(add)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    purchase?.addEventListener('submit', preventRepeatedAdd, true);

    const updateOptions = () => {
      const picker = product.querySelector('variant-selects');
      if (!picker) return;
      const variants = JSON.parse(picker.querySelector('script[type="application/json"]').textContent);
      const fields = Array.from(picker.querySelectorAll('fieldset'));
      const selected = fields.map(field => field.querySelector('input:checked')?.value);
      fields.forEach((field, index) => {
        field.querySelectorAll('[data-listing-value]').forEach(label => {
          const variant = variants.find(item => item.options[index] === label.dataset.listingValue && item.options.every((value, position) => position === index || value === selected[position]));
          label.dataset.listingOptionAvailable = String(!!variant?.available);
          const price = label.querySelector('[data-listing-option-price]');
          if (price) price.textContent = variant ? money(variant.price, product.dataset.currency) : (window.ieGeekListingStrings?.unavailable || 'Unavailable');
          const compare = label.querySelector('[data-listing-option-compare]');
          const sale = label.querySelector('[data-listing-option-sale]');
          const discounted = variant && variant.compare_at_price > variant.price;
          if (compare) { compare.hidden = !discounted; if (discounted) compare.textContent = money(variant.compare_at_price, product.dataset.currency); }
          if (sale) {
            sale.hidden = !discounted;
            if (discounted) {
              const percentage = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
              sale.textContent = window.ieGeekListingText?.('discountFormat', { percentage }) || percentage + '% OFF';
            }
          }
        });
      });
      // The theme uses native horizontal scrolling for the mobile gallery.
      const variant = variants.find(item => item.options.every((value, index) => value === selected[index]));
      const gallery = product.querySelector('.product-images');
      if (mobile.matches && variant?.featured_media && gallery) {
        const slide = gallery.querySelector('[data-media-id$="-'+variant.featured_media.id+'"]');
        if (slide) gallery.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
      }
    };
    picker?.addEventListener('change', updateOptions);
    const updateAvailability = event => {
      if (String(event.detail?.sectionId) !== picker?.dataset.section) return;
      const variant = event.detail.variant;
      const state = !variant ? 'unavailable' : variant.available ? 'available' : 'sold-out';
      product.dataset.listingAvailability = state;
      if (add) {
        add.classList.toggle('sold-out', state !== 'available');
        const label = add.querySelector('.single-add-to-cart-button--text');
        const preorder = add.closest('product-form')?.getAttribute('template')?.includes('pre-order');
        if (label) label.textContent = state === 'unavailable' ? window.theme.variantStrings.unavailable : state === 'sold-out' ? window.theme.variantStrings.soldOut : preorder ? window.theme.variantStrings.preOrder : window.theme.variantStrings.addToCart;
      }
      updateOptions();
      syncSticky();
    };
    document.addEventListener('product:variant-change', updateAvailability);
    updateOptions();

    const description = product.querySelector('.listing-description');
    const descriptionContent = description?.querySelector('.listing-description__content');
    let descriptionObserver;
    if (descriptionContent) {
      const updateDescription = () => {
        if (description.classList.contains('is-expanded')) return;
        const overflow = descriptionContent.scrollHeight > descriptionContent.clientHeight + 1;
        description.classList.toggle('has-overflow', overflow);
        description.querySelector('button').hidden = !overflow;
      };
      updateDescription();
      descriptionObserver = new ResizeObserver(updateDescription);
      descriptionObserver.observe(descriptionContent);
      document.fonts?.ready.then(() => { if (product.isConnected) updateDescription(); });
    }


    // Dispose listeners when Shopify reloads the section in the theme editor.
    const unload = event => {
      if (!event.target.contains(product)) return;
      buttonObserver.disconnect();
      purchase?.removeEventListener('submit', preventRepeatedAdd, true);
      picker?.removeEventListener('change', updateOptions);
      document.removeEventListener('product:variant-change', updateAvailability);
      descriptionObserver?.disconnect();
      cleanupGallery();
      document.removeEventListener('shopify:section:unload', unload);
    };
    document.addEventListener('shopify:section:unload', unload);
  }

  function initGallery(product) {
    const slider = product.querySelector('product-slider');
    const progress = product.querySelector('.listing-gallery-progress');
    const indicator = product.querySelector('.product-slide-indicator');
    if (!slider || !progress || !indicator) return () => {};
    let frame;
    const update = () => {
      frame = null;
      const slides = Array.from(slider.querySelectorAll('.product-images__slide.is-active'));
      const total = slides.length;
      let index = 0;
      if (slider.flkty) index = slider.flkty.selectedIndex;
      else if (total) {
        const left = slider.getBoundingClientRect().left;
        index = slides.reduce((nearest, slide, current) => Math.abs(slide.getBoundingClientRect().left - left) < Math.abs(slides[nearest].getBoundingClientRect().left - left) ? current : nearest, 0);
      }
      index = Math.max(0, Math.min(index, total - 1));
      indicator.hidden = progress.hidden = total < 2;
      indicator.querySelector('.current-slide').textContent = index + 1;
      indicator.querySelector('.total-slides').textContent = total;
      progress.style.setProperty('--listing-gallery-segment', 100 / Math.max(1, total) + '%');
      progress.style.setProperty('--listing-gallery-offset', index * 100 + '%');
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    slider.addEventListener('scroll', schedule, { passive: true });
    slider.flkty?.on('select', schedule);
    const observer = new MutationObserver(schedule);
    observer.observe(slider, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    const sizeObserver = new ResizeObserver(schedule);
    sizeObserver.observe(slider);
    update();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      sizeObserver.disconnect();
      slider.removeEventListener('scroll', schedule);
      slider.flkty?.off('select', schedule);
    };
  }

  // Theme-editor reloads replace individual page modules while the main product stays mounted.
  function initSections() {
    cleanupSections?.();
    const categories = Array.from(document.querySelectorAll('[data-listing-spec-category]'));
    const updateCategories = () => {
      const seen = new Set();
      categories.forEach(item => {
        const group = item.closest('[data-listing-spec-panel]') || item.closest('.listing-specs');
        item.open = !mobile.matches || !seen.has(group);
        seen.add(group);
      });
    };
    updateCategories();
    mobile.addEventListener('change', updateCategories);
    const specTabs = Array.from(document.querySelectorAll('[data-listing-spec-tab]'));
    const activateSpecTab = (tab, focus = false) => {
      const root = tab.closest('.listing-specs');
      root.querySelectorAll('[data-listing-spec-tab]').forEach(button => {
        const active = button === tab;
        button.setAttribute('aria-selected', String(active)); button.tabIndex = active ? 0 : -1;
      });
      root.querySelectorAll('[data-listing-spec-panel]').forEach(panel => { panel.hidden = panel.id !== tab.getAttribute('aria-controls'); });
      if (focus) tab.focus({ preventScroll: true });
    };
    const onSpecTab = event => {
      const tab = event.target.closest('[data-listing-spec-tab]');
      if (!tab) return;
      if (event.type === 'click') { activateSpecTab(tab); return; }
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const tabs = Array.from(tab.closest('[role="tablist"]').querySelectorAll('[data-listing-spec-tab]'));
      const index = tabs.indexOf(tab);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      activateSpecTab(tabs[next], true);
    };
    specTabs.forEach(tab => { tab.addEventListener('click', onSpecTab); tab.addEventListener('keydown', onSpecTab); });

    const nav = document.querySelector('[data-listing-nav]');
    const anchors = new Map();
    document.querySelectorAll('[data-listing-anchor]').forEach(element => anchors.set(element.dataset.listingAnchor, element));
    const faq = document.querySelector('.faq-section-wrapper');
    const reviews = document.querySelector('.Products-Customer-Reviews, .apps-outer:has(.jdgm-review-widget), #judgeme_product_reviews');
    if (faq) anchors.set('faqs', faq);
    if (reviews) anchors.set('reviews', reviews.closest('.shopify-section') || reviews);
    // Each product template keeps its own overview and app section instances.
    ['overview', 'specs', 'compare', 'faqs', 'reviews'].forEach(key => {
      const sectionId = nav?.getAttribute('data-listing-' + key + '-section');
      const prefix = nav?.dataset.listingTemplatePrefix || '';
      const target = sectionId && (document.getElementById('shopify-section-' + prefix + sectionId) || document.getElementById('shopify-section-' + sectionId));
      if (target) target.classList.add('listing-anchor');
      if (target && !anchors.has(key)) anchors.set(key, target);
    });
    nav?.querySelectorAll('[data-listing-link]').forEach(link => {
      const target = anchors.get(link.dataset.listingLink);
      if (!target) { link.hidden = true; return; }
      link.hidden = false;
      if (!target.id) target.id = 'listing-' + link.dataset.listingLink;
      link.href = '#' + target.id;
    });
    if (nav) nav.hidden = !nav.querySelector('[data-listing-link]:not([hidden])');
    const reviewTriggers = Array.from(document.querySelectorAll('[data-listing-review-trigger]'));
    reviewTriggers.forEach(trigger => {
      if (anchors.has('reviews')) {
        trigger.setAttribute('role', 'link'); trigger.tabIndex = 0;
        trigger.setAttribute('aria-label', window.ieGeekListingStrings?.readReviews || 'Read product reviews');
      } else {
        trigger.removeAttribute('role'); trigger.removeAttribute('tabindex'); trigger.removeAttribute('aria-label');
      }
    });
    const onReviewTrigger = event => {
      if (!(event.target instanceof Element) || !event.target.closest('[data-listing-review-trigger]')) return;
      if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
      const target = anchors.get('reviews');
      if (!target) return;
      event.preventDefault(); event.stopPropagation();
      const link = nav?.querySelector('[data-listing-link="reviews"]');
      if (link) link.click();
      else target.scrollIntoView({ behavior: 'instant', block: 'start' });
    };
    document.addEventListener('click', onReviewTrigger, true);
    document.addEventListener('keydown', onReviewTrigger, true);
    const headerSection = document.querySelector('.header-section');
    const header = headerSection?.querySelector('theme-header, .header') || document.querySelector('#header');
    let navFrame = null;
    let headerTransition = false;
    const updateNav = () => {
      navFrame = null;
      // The wrapper remains in place while the header itself slides off screen.
      const headerHeight = header ? Math.max(0, Math.min(header.getBoundingClientRect().bottom, header.offsetHeight)) : 0;
      document.documentElement.style.setProperty('--listing-header-height', headerHeight + 'px');
      const links = Array.from(nav?.querySelectorAll('[data-listing-link]:not([hidden])') || []);
      let active = links[0]?.dataset.listingLink;
      let nearest = -Infinity;
      const offset = headerHeight + (nav?.offsetHeight || 54) + 26;
      links.forEach(link => {
        const target = anchors.get(link.dataset.listingLink);
        const top = target?.getBoundingClientRect().top;
        if (top <= offset && top > nearest) { nearest = top; active = link.dataset.listingLink; }
      });
      nav?.querySelectorAll('[data-listing-link]').forEach(link => {
        if (link.dataset.listingLink === active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      if (headerTransition) onScroll();
    };
    const onScroll = () => { if (navFrame === null) navFrame = requestAnimationFrame(updateNav); };
    const onHeaderTransition = event => {
      if (event.target !== header || event.propertyName !== 'transform') return;
      headerTransition = event.type === 'transitionrun';
      onScroll();
    };
    const headerObserver = new MutationObserver(onScroll);
    if (headerSection) headerObserver.observe(headerSection, { attributes: true, attributeFilter: ['class', 'style'] });
    const headerSizeObserver = new ResizeObserver(onScroll);
    if (header) {
      headerSizeObserver.observe(header);
      ['transitionrun', 'transitionend', 'transitioncancel'].forEach(type => header.addEventListener(type, onHeaderTransition));
    }
    const onNavigate = event => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('[data-listing-link]');
      if (!link || !nav.contains(link)) return;
      const target = anchors.get(link.dataset.listingLink);
      if (!target) return;
      event.preventDefault();
      event.stopPropagation();
      const targetTop = target.getBoundingClientRect().top;
      const navHeight = nav.offsetHeight;
      const stickyHeaderHeight = header?.classList.contains('header-sticky--active') ? header.offsetHeight : 0;
      // Upward navigation, open menus and keyboard focus keep the header visible.
      // Reserve its final height, rather than the current slide-out frame.
      const keepHeaderVisible = document.body.matches('.mobile-menu-open, .body-megamenu-open') || header?.matches(':focus-within');
      const revealHeader = keepHeaderVisible || targetTop < stickyHeaderHeight + navHeight + 24;
      const offset = navHeight + 24 + (revealHeader ? stickyHeaderHeight : 0);
      if (location.hash !== link.hash) history.pushState(history.state, '', link.hash);
      window.scrollTo({ top: Math.max(0, window.scrollY + targetTop - offset), behavior: 'instant' });
      onScroll();
    };
    nav?.addEventListener('click', onNavigate);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    updateNav();
    initComparison();
    cleanupSections = () => {
      mobile.removeEventListener('change', updateCategories);
      specTabs.forEach(tab => { tab.removeEventListener('click', onSpecTab); tab.removeEventListener('keydown', onSpecTab); });
      document.removeEventListener('click', onReviewTrigger, true);
      document.removeEventListener('keydown', onReviewTrigger, true);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      headerTransition = false;
      cancelAnimationFrame(navFrame);
      headerObserver.disconnect();
      headerSizeObserver.disconnect();
      ['transitionrun', 'transitionend', 'transitioncancel'].forEach(type => header?.removeEventListener(type, onHeaderTransition));
      nav?.removeEventListener('click', onNavigate);
    };
  }

  async function initComparison() {
    const comparison = document.querySelector('.listing-compare');
    const local = document.querySelector('script[data-product-compare-specs]');
    if (comparison && comparison === activeComparison && local === comparisonSource) return;
    cleanupComparison?.();
    cleanupComparison = null;
    activeComparison = comparison;
    comparisonSource = local;
    if (!comparison) return;
    comparison.dataset.initialized = 'true';
    let disposed = false;
    const listeners = [];
    const selects = Array.from(comparison.querySelectorAll('[data-listing-compare-select]'));
    cleanupComparison = () => {
      disposed = true;
      listeners.forEach(([select, listener]) => select.removeEventListener('change', listener));
      selects.forEach(select => { select.dataset.request = Number(select.dataset.request || 0) + 1; });
      delete comparison.dataset.initialized;
    };
    await customElements.whenDefined('product-compare-table');
    if (disposed) return;
    const loader = document.createElement('product-compare-table');
    const localPayload = local ? JSON.parse(local.textContent) : null;
    const status = comparison.querySelector('[data-listing-compare-status]');
    const failedColumns = new Set();
    const aliases = {
      'DC Power': ['DC Power', 'Power Supply', 'Power  Supply', 'Power Source', 'Camera Power'],
      'Night vision': ['Night vision', 'Night Vision (Infrared)', 'Infrared Night Vision', 'Night Vision Distance'],
      'Color Night vision': ['Color Night vision', 'Full Color Night Vision', 'Color Night Vision Distance'],
      'Battery Capacity': ['Battery Capacity', 'Battery', 'Battery Size'],
      'Storage Methods': ['Storage Methods', 'Storage', 'Video Storage'],
      'Waterproof': ['Waterproof', 'Waterproof Rating', 'IP Rating'],
      'APP': ['APP', 'App', 'Compatible App'],
      'Two Way Audio': ['Two Way Audio', 'Two-Way Audio', 'Two Way Talk', 'Two-Way Talk'],
      'PIR Detection': ['PIR Detection', 'PIR Detection Distance', 'Detection Range']
    };
    const update = async select => {
      if (disposed) return;
      const column = select.dataset.column;
      const request = Number(select.dataset.request || 0) + 1;
      select.dataset.request = request;
      select.setAttribute('aria-busy', 'true');
      let payload = null;
      try {
        const currentPath = localPayload?.product?.url;
        const url = loader.getProductUrl(select.value);
        payload = select.value === currentPath ? localPayload : await loader.getSpecPayload(url);
        if (Number(select.dataset.request) !== request || !comparison.isConnected) return;
        const card = comparison.querySelector('[data-listing-compare-card="'+column+'"]');
        if (payload?.cardHtml) {
          card.innerHTML = payload.cardHtml;
          failedColumns.delete(column);
        }
        else {
          card.textContent = window.ieGeekListingStrings?.compareLoadError || 'Product details unavailable. Please try again.';
          failedColumns.add(column);
          loader.payloadCache.delete(url);
          const retry = document.createElement('button');
          retry.type = 'button';
          retry.className = 'listing-compare__retry';
          retry.textContent = window.ieGeekListingStrings?.compareRetry || 'Retry';
          retry.addEventListener('click', () => update(select));
          card.appendChild(retry);
        }
        status.textContent = failedColumns.size ? (window.ieGeekListingStrings?.comparePartialError || 'Some product details could not be loaded.') : '';
        const items = payload ? loader.createItemIndex(loader.getMainSpecItems(payload.items)) : new Map();
        comparison.querySelectorAll('[data-listing-compare-field]').forEach(row => {
          const label = row.dataset.listingCompareField;
          const existing = window.ieGeekProductCompareFields?.find(field => field.aliases.includes(label));
          const fieldAliases = aliases[label] || existing?.aliases || [label];
          const value = payload ? loader.resolveFieldValue({ aliases: fieldAliases }, items) : 'N/A';
          row.querySelector('[data-listing-compare-cell="'+column+'"]').textContent = value;
        });
      } finally {
        if (Number(select.dataset.request) === request) select.removeAttribute('aria-busy');
      }
    };
    selects.forEach(select => {
      const listener = () => update(select);
      select.addEventListener('change', listener);
      listeners.push([select, listener]);
      update(select);
    });
  }

  document.addEventListener('click', async event => {
    const description = event.target.closest('[data-listing-description]');
    if (description) {
      const expanded = description.getAttribute('aria-expanded') !== 'true';
      description.setAttribute('aria-expanded', expanded);
      description.closest('.listing-description').classList.toggle('is-expanded', expanded);
      const label = description.querySelector('[data-listing-description-label]');
      if (label) label.textContent = expanded ? (window.ieGeekListingStrings?.viewLess || 'View Less') : (window.ieGeekListingStrings?.viewMore || 'View More');
    }
    const action = event.target.closest('[data-listing-action]');
    if (action && !action.disabled) {
      const purchase = document.querySelector('[data-listing-purchase]');
      const target = purchase?.querySelector(action.dataset.listingAction === 'add' ? 'button[name="add"]' : '.buy-now-button');
      const form = purchase?.querySelector('form');
      if (target && !target.disabled && !target.classList.contains('loading') && target.getAttribute('aria-disabled') !== 'true' && form?.reportValidity()) target.click();
    }
    const copy = event.target.closest('[data-listing-copy]');
    if (copy) {
      const status = copy.parentElement.querySelector('[data-listing-copy-status]');
      try {
        await navigator.clipboard.writeText(copy.dataset.listingCopy);
        status.textContent = window.ieGeekListingStrings?.codeCopied || 'Code copied';
      } catch (error) {
        status.textContent = window.ieGeekListingStrings?.codeCopyError || 'Could not copy. Select the code and copy it manually.';
      }
    }
  });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => {
    cleanupSections?.();
    cleanupSections = null;
    if (activeComparison && event.target.contains(activeComparison)) {
      cleanupComparison?.();
      cleanupComparison = null;
      activeComparison = null;
      comparisonSource = null;
    }
    // Deleting a section emits unload without a later load event.
    requestAnimationFrame(() => { if (document.querySelector('[data-listing-product]')) init(); });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
